import { createElement } from "react";

import { Coding, Observation } from "@medplum/fhirtypes";

import { LOINCCodes } from "@/fhir/enums/loinc";

interface RowComponentProps {
  time: string;
  observation: Observation;
  showNumbersOnly: boolean;
  flowsheetId?: string;
  onRefresh?: () => Promise<void>;
  isViewOnly: boolean;
}

interface PostpartumTableRowsProps {
  time: string;
  observations: Observation[];
  flowsheetForms: Record<string, string>;
  rowComponentByForm: Record<string, React.ComponentType<RowComponentProps>>;
  showNumbersOnly: boolean;
  flowsheetId?: string;
  onRefresh?: () => Promise<void>;
  isViewOnly: boolean;
}

export function PostpartumTableRows({
  time,
  observations,
  flowsheetForms,
  rowComponentByForm,
  showNumbersOnly,
  flowsheetId,
  onRefresh,
  isViewOnly,
}: PostpartumTableRowsProps): React.ReactElement[] {
  // Keep track of processed observation IDs to avoid duplicates
  const processedIds = new Set<string>();

  // Sort observations to ensure CBL appears before EBL when they have the same timestamp
  const sortedObservations = [...observations].sort((a, b) => {
    const aCode = a.code?.coding?.[0]?.code;
    const bCode = b.code?.coding?.[0]?.code;

    // CBL should come before EBL
    const aIsCbl = aCode === LOINCCodes.CumulativeBloodLoss;
    const bIsCbl = bCode === LOINCCodes.CumulativeBloodLoss;

    if (aIsCbl && !bIsCbl) return -1; // CBL comes first
    if (!aIsCbl && bIsCbl) return 1; // CBL comes first

    // Otherwise maintain original order
    return 0;
  });

  const observationRows = sortedObservations
    .filter((observation) => {
      if (!observation.id || processedIds.has(observation.id)) return false;
      processedIds.add(observation.id);
      return true;
    })
    .map((observation) => {
      const code = observation.code?.coding?.find(
        (coding: Coding) =>
          Object.values(flowsheetForms).some((formCode) => coding.code?.startsWith(formCode)) ||
          Object.keys(rowComponentByForm).includes(coding.code ?? ""),
      )?.code as keyof typeof rowComponentByForm;
      const RowComponent = code ? rowComponentByForm[code] : null;

      if (!RowComponent) return null;
      return createElement(RowComponent, {
        key: `${observation.id}-${flowsheetId}`,
        time,
        observation,
        showNumbersOnly,
        flowsheetId,
        onRefresh,
        isViewOnly,
      });
    })
    .filter((element): element is React.ReactElement => element !== null);

  return observationRows;
}
